'use client';

import { Icon } from '@magicschool/spellbook/components/Icon';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import { Label } from '@magicschool/spellbook/components/Label';
import { SearchInputLeadingAdornmentSlot } from '@magicschool/spellbook/components/SearchInput/slots';
import type { TextInputProps } from '@magicschool/spellbook/components/TextInput';
import { TextInput } from '@magicschool/spellbook/components/TextInput';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

export interface SearchInputText {
  /** Text for the clear search button. Defaults to 'Clear search'. */
  clearSearch?: string;
  /** The label for the input. Defaults to 'Search'. */
  label?: string;
}

export type SearchInputProps = Omit<TextInputProps, 'type' | 'value' | 'defaultValue'> & {
  /** When `true`, renders the label visibly above the input. When `false` (default), the label is visually hidden via `sr-only`. */
  showLabel?: boolean;
  /** Controlled input value. When provided, the clear button appears if the value is non-empty. */
  value?: string;
  /** Uncontrolled default value. The component tracks internal state for clear button visibility. */
  defaultValue?: string;
  /** Callback fired when the clear button is clicked. Pair with controlled `value` to reset external state. */
  onClearSearch?: () => void;
  /** Additional class name applied to the outer wrapper `div`. */
  wrapperClassName?: string;
  /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
  text?: SearchInputText;
};

const SearchInputBase = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      children,
      className,
      id: providedId,
      showLabel = false,
      size = 'md',
      defaultValue,
      value,
      onClearSearch,
      onInput,
      wrapperClassName,
      text = {},
      ...props
    },
    forwardedRef,
  ) => {
    const { clearSearch = 'Clear search', label = 'Search' } = text;
    const id = useId(providedId);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [internalValue, setInternalValue] = useState(defaultValue ?? '');
    const isControlled = value !== undefined;
    const hasText = isControlled ? Boolean(value) : Boolean(internalValue);

    const handleClearSearch = () => {
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.focus();
      }
      if (!isControlled) {
        setInternalValue('');
      }

      onClearSearch?.();
    };

    const handleOnInput = (e: React.FormEvent<HTMLInputElement>) => {
      const inputValue = (e.target as HTMLInputElement).value;
      if (!isControlled) {
        setInternalValue(inputValue);
      }
      onInput?.(e);
    };

    const { slots } = useSlots(children, {
      leadingAdornment: { Component: SearchInputLeadingAdornmentSlot, props: { size, ...(props.disabled && { color: 'disabled' }) } },
    });

    useImperativeHandle(forwardedRef, () => inputRef.current!, []);

    return (
      <div className={cn('flex flex-col gap-1 w-full group/search-input', wrapperClassName)} data-spellbook-name="search-input-wrapper">
        <Label {...(!showLabel && { className: 'sr-only' })} htmlFor={id}>
          {label}
        </Label>
        <TextInput
          data-spellbook-name="search-input"
          ref={inputRef}
          type="search"
          id={id}
          className={className}
          {...props}
          onInput={handleOnInput}
          {...(isControlled ? { value } : { defaultValue })}
          size={size}
        >
          <TextInput.LeadingAdornment overrideDefault>{slots.leadingAdornment}</TextInput.LeadingAdornment>
          <TextInput.TrailingAdornment overrideDefault>
            {hasText ? (
              <IconButton
                name="x"
                color="neutral"
                variant="ghost"
                shape="circle"
                size="sm"
                label={clearSearch}
                onClick={handleClearSearch}
              />
            ) : (
              <Icon
                name="search"
                color={props.disabled ? 'disabled' : 'default'}
                size="sm"
                className={cn('m-1 group-focus-within/search-input:stroke-primary-icon')}
              />
            )}
          </TextInput.TrailingAdornment>
        </TextInput>
      </div>
    );
  },
);

/**
 * Specialized search input built on `TextInput` with a built-in search icon, clear button, and accessible label.
 *
 * ### When to use
 * - Search fields that need a clear button and search icon out of the box
 * - Any search or filter input where the built-in UX patterns (icon swap, clear action) are desired
 *
 * ### When NOT to use
 * - General-purpose text entry: use `TextInput` instead
 * - Multi-line text entry: use `Textarea` instead
 * - Custom search UIs that need full control over trailing adornments: use `TextInput` with `type="search"` instead
 *
 * ### API
 * - `size`: Input dimensions: `md` (default), `lg` for prominent search fields, or `xl` for extra-large inputs
 * - `showLabel`: When `true`, renders the label visibly above the input. When `false` (default), the label is visually hidden but remains accessible via `sr-only`.
 * - `value`: Controlled input value. When provided, the clear button appears if the value is non-empty.
 * - `defaultValue`: Uncontrolled default value. The component tracks internal state for clear button visibility.
 * - `onClearSearch`: Callback fired when the clear button is clicked. Pair with controlled `value` to reset external state.
 * - `text.label`: Accessible label text. Defaults to "Search".
 * - `text.clearSearch`: Clear button accessible label. Defaults to "Clear search".
 *
 * ### Composition (slots)
 * - `<SearchInput.LeadingAdornment>`: Icon before the input. Accepts `IconProps`. Only include when adding a custom leading icon — not rendered by default (a search icon is shown in the trailing position instead).
 *
 * ### Visual states
 * - **Empty**: Trailing search icon displayed
 * - **Has text**: Search icon replaced with a clear (X) button
 * - **Disabled**: Muted styling, no clear button interaction
 * - **Focus**: Border changes to `focus-ring`, search icon color changes to primary
 *
 * ### Accessibility
 * - Always includes an accessible `<Label>` (visible or `sr-only` based on `showLabel`)
 * - Configure `text.label` for localized label text
 * - Configure `text.clearSearch` for localized clear button label
 */
export const SearchInput = Object.assign(SearchInputBase, {
  LeadingAdornment: SearchInputLeadingAdornmentSlot,
});
